import type { SnapshotMeta } from '@/document/model'
import type { VoxelMap } from '@/voxelTypes'
import type { EventTuple, ExperienceEvent, ToolType } from './types'

/*
 * Typed builders for the event tuples pages and the page kit dispatch most, so no call site needs
 * an `as unknown as EventTuple` cast. Pure (types only): the reducer's own imports may use them.
 * Dispatching undo is `undo(store)` in src/events/atoms.ts.
 */

export type BlendOp = 'place' | 'fill' | 'subtract' | 'mask' | 'xor' | 'paint'

/** Blend cells into the document: a map of cells (place, fill, paint, ...) or keys (subtract). */
export const blend = (cells: VoxelMap | number[], op: BlendOp): EventTuple => ['voxel.blend', cells, op]

/** Set one field of the document meta by path (`undefined` removes it). */
export const metaSetIn = (path: string[], value: unknown): EventTuple => ['meta.setIn', path, value]

/** Merge fields into the document meta. */
export const metaSet = (fields: Partial<SnapshotMeta>): EventTuple => ['meta.set', fields]

/** Merge fields into the ephemeral ui state (not undoable). */
export const uiSet = (fields: Record<string, unknown>): EventTuple => ['ui.set', fields]

/** Set one field of the ephemeral ui state by path. */
export const uiSetIn = (path: string[], value: unknown): EventTuple => ['ui.setIn', path, value]

export const uiSetTool = (tool: ToolType): EventTuple => ['ui.setTool', tool]

export const uiPaletteIndex = (index: number): EventTuple => ['ui.tool.paletteIndex', index]

/** A page's (or any durable experience's) own event, `experience.<page>.<verb>`. */
export const experienceEvent = (name: `experience.${string}`, payload: unknown): ExperienceEvent => [name, payload]
